import { CreateTenantDto, QueryFactory } from '@lmt/shared-types';

import {
  Field,
  FieldProps,
} from '@lmt/unicorn-shared/app/components/common/Field';
import { SubmitButton } from '@lmt/unicorn-shared/app/components/common/ui-kit/buttons/SubmitButton';
import { Input } from '@lmt/unicorn-shared/app/components/common/ui-kit/inputs/Input';
import { pages } from '@lmt/unicorn-shared/app/constants/sharedRoutes';
import { useUniDispatch } from '@lmt/unicorn-shared/app/store/types';
import { useModal } from '@lmt/unicorn-shared/app/components/common/ui-kit/modal/useModal';
import {
  emailRule,
  requiredRule,
  usernameRule,
} from '@lmt/unicorn-shared/app/helpers/form/react-hook-form-helper';
import {
  handleSubmitFormError,
  useErrorHandler,
} from '@lmt/unicorn-shared/app/helpers/form/useErrorHandler';
import { useScopedTranslation } from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';
import { nameofFactory } from '@lmt/unicorn-shared/app/helpers/nameof';
import React, { FC, useCallback } from 'react';
import { useForm, UseFormSetError } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import localStyles from './CreateTenant.module.scss';
import styles from '@lmt/unicorn-shared/app/components/global-forms/Content.module.scss';

const nameof = nameofFactory<CreateTenantDto>();

const StyledField = (props: FieldProps) => {
  return <Field className={styles.field} {...props} required={true} />;
};

/*
On backend errors for 'adminPassword' and 'adminUsername' are returned for 'password' and 'username' fields.
We need to correct this to show error under appropriate field.
(testing scenario: try to create tenant with simple password like '123')
 */
function mapFormPropertyNameForErrorHandling(
  fieldName: string,
): keyof CreateTenantDto {
  if (fieldName === 'password') {
    return nameof('adminPassword');
  }
  if (fieldName === 'username') {
    return nameof('adminUsername');
  }
  return fieldName as any;
}

export const CreateTenant: FC = () => {
  const i18next = useScopedTranslation('tenants.create_tenant_form');
  const modals = useModal();

  const { register, handleSubmit, formState, setError, reset } =
    useForm<CreateTenantDto>({
      shouldFocusError: true,
      criteriaMode: 'all',
    });

  const setErrorWithRemappedProperties: UseFormSetError<CreateTenantDto> =
    useCallback(
      (name, error) => {
        setError(mapFormPropertyNameForErrorHandling(name), error);
      },
      [setError],
    );

  const submitForm = useErrorHandler<CreateTenantDto>(
    useCallback(async (data, navigate) => {
      try {
        const tenantDto = await QueryFactory.TenantClient.createTenant(data);

        await modals.showAlert({
          title: i18next.t('message_tenant_created_title'),
          text: i18next.t('message_tenant_created', {
            tenant: tenantDto.domain,
          }),
        });
        navigate(pages.home.link());
      } catch (e) {
        const errorDetails = handleSubmitFormError(e, setError);
        if (errorDetails.overallServerError) {
          await modals.showError({ text: errorDetails.overallServerError });
        }
      }
    }, []),
    setErrorWithRemappedProperties,
  );

  return (
    <form
      className={localStyles.container}
      onSubmit={handleSubmit(submitForm.handler)}
    >
      <div className={localStyles.dataContainer}>
        <StyledField
          title={i18next.t('access_passphrase')}
          className={localStyles.rowField}
        >
          <Input
            {...register(nameof('accessPassphrase'), requiredRule())}
            errorText={formState.errors.accessPassphrase?.message}
          />
        </StyledField>
        <StyledField title={i18next.t('name')} className={localStyles.rowField}>
          <Input
            {...register(nameof('name'), requiredRule())}
            errorText={formState.errors.name?.message}
          />
        </StyledField>
        <StyledField title={i18next.t('domain')}>
          <Input
            {...register(nameof('domain'), requiredRule())}
            errorText={formState.errors.domain?.message}
          />
        </StyledField>
        <div className={localStyles.header}>{i18next.t('admin_user')}</div>
        <StyledField title={i18next.t('admin_first_name')}>
          <Input
            {...register(nameof('adminFirstName'), requiredRule())}
            errorText={formState.errors.adminFirstName?.message}
          />
        </StyledField>
        <StyledField title={i18next.t('admin_last_name')}>
          <Input
            {...register(nameof('adminLastName'), requiredRule())}
            errorText={formState.errors.adminLastName?.message}
          />
        </StyledField>
        <Field className={styles.field} title={i18next.t('admin_email')}>
          <Input
            {...register(nameof('adminEmail'), {
              ...emailRule(),
            })}
            errorText={formState.errors.adminEmail?.message}
          />
        </Field>
        <StyledField title={i18next.t('admin_username')}>
          <Input
            {...register(nameof('adminUsername'), {
              ...requiredRule(),
              ...usernameRule(),
            })}
            errorText={formState.errors.adminUsername?.message}
          />
        </StyledField>
        <StyledField title={i18next.t('admin_password')}>
          <Input
            type="password"
            {...register(nameof('adminPassword'), requiredRule())}
            errorText={formState.errors.adminPassword?.message}
          />
        </StyledField>
        <div className={localStyles.error}>
          {submitForm.overallServerError && (
            <p>{submitForm.overallServerError}</p>
          )}
        </div>
      </div>

      <div className={styles.submitContainer}>
        <SubmitButton
          submitting={formState.isSubmitting}
          className={styles.submit}
          title="Create"
        />
      </div>
    </form>
  );
};
